import React, { useContext, useEffect, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import cn from 'classnames'
import currency from 'currency.js'
import { CustomerContext } from '../../contexts/CustomerContext'
import EmptyCart from './EmptyCart/EmptyCart'
import Grid from '../Grid'
import CartItem from './CartItem/v2';
import SummaryItem from './SummaryItem/SummaryItem'
import { AddonSheet, Cart, OrderPackage } from '../../models/_models'
import styles from './CustomerCart.module.sass'
import Button from '../Button'
import V2Button from '../Button/v2';
import Spinner from '../Spinner'
import Typography from '../Typography'
import routes from '../../routes';
import { OrderPackageExtra } from '../../models'
import { useQueryClient } from '@tanstack/react-query'
import QUERY_KEYS from '../../utils/QueryKeys'
import cartItemStyles from './CartItem/v2/CartItem.module.scss';
import deleteImage from '../../../assets/images/delete.png';
import FullResDownloads from './FullResDownloads';
import Modal from '../common/Modal';
import { wouldStrandUpsell, orphanedUpsellIds } from './buyAllUpsell';
import { mergeCartPage } from './mergeCartPage';
import { couponSummary } from './couponSummary';
import couponSummaryLine from './couponSummaryLine';

const useCustomerData = (providedCart, setIsLoading, isPurchased) => {
  const { customer, setCustomer } = useContext(CustomerContext)
  const history = useHistory()
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const [numFetches, setNumFetches] = useState(0)
  const [cart, setCart] = useState(providedCart || customer.cart)
  const [page, setPage] = useState(1)

  const queryClient = useQueryClient();

  // Enhanced effect to detect navigation from edit pages
  useEffect(() => {
    let shouldRefetch = false;
    
    // Check localStorage flags
    const needsRefresh = localStorage.getItem('needsCartRefresh');
    const lastEditTimestamp = localStorage.getItem('lastEditTimestamp');
    
    if (needsRefresh === 'true' || lastEditTimestamp) {
      shouldRefetch = true;
      localStorage.removeItem('needsCartRefresh');
      localStorage.removeItem('lastEditTimestamp');
    }
    
    // Check URL params
    const urlParams = new URLSearchParams(location.search);
    const refreshParam = urlParams.get('refresh');
    
    if (refreshParam) {
      shouldRefetch = true;
      
      // Clean up the URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
    
    if (shouldRefetch) {
      console.log('Triggering cart refetch due to edit completion');
      setNumFetches(prev => prev + 1);
      setPage(1); // Reset pagination
      setCart(null); // Clear current cart to force fresh fetch
    }
  }, [location.search, location.pathname]);

  const removeItem = async (id, type) => {
    switch (type) {
      case 'orderPackage': {
        const op = cart.orderPackages.find((orderPackage) => orderPackage.id === id);
        if (op) {
          const orderPackage = new OrderPackage({ id, cartId: cart.id });
          orderPackage.isPersisted = true;

          await orderPackage.destroy();

          let remainingPackages = cart.orderPackages.filter((t) => t.id.toString() !== id.toString());
          // If that was this student's last base package, the server auto-removes
          // their now-orphaned senior "Buy All" upsell — mirror it locally so the
          // cart re-renders without the stranded upsell.
          const orphanIds = orphanedUpsellIds(remainingPackages, op.studentId);
          if (orphanIds.length) {
            remainingPackages = remainingPackages.filter((t) => !orphanIds.includes(t.id));
          }
          cart.orderPackages = remainingPackages;

          // Recompute the Past Images volume-discount coupon — the tier
          // may have dropped a step (or the coupon may need to be detached
          // entirely if count fell below 2). Failure is non-fatal; the
          // cart still re-renders without us blocking on it.
          try {
            const { refreshVolumeDiscount } = await import('../../api/carts');
            await refreshVolumeDiscount(cart.id);
          } catch (couponErr) {
            // eslint-disable-next-line no-console
            console.error('removeItem: refreshVolumeDiscount failed', couponErr);
          }

          setNumFetches(numFetches + 1);
          setCustomer({ ...customer, cart });
          setCart(cart);

        }
        break
      }
      case 'extras': {
        const orderPackageExtra = cart
          .orderPackages
          .map((op) => op.orderPackageExtras)
          .flat()
          .find((ope) => ope.id.toString() === id.toString());

        const ope = new OrderPackageExtra({ id });

        await ope.destroy();

        const newOrderPackages = cart.orderPackages.map((op) => {
          const orderPackageExtras = op.orderPackageExtras.filter((ope) => ope.id.toString() !== orderPackageExtra.id.toString());

          return { ...op, orderPackageExtras };
        });

        cart.orderPackages = newOrderPackages;
        setNumFetches(numFetches + 1);
        setCustomer({ ...customer, cart });
        setCart(cart);

        break;
      }
      case 'addon-sheets': {
        const as = cart
          .orderPackages
          .map((orderPackage) => orderPackage.addonSheets)
          .flat()
          .find((addonSheet) => addonSheet.id === id)

        if (as) {
          const addonSheet = new AddonSheet({ id, orderPackageId: as.orderPackageId });
          addonSheet.isPersisted = true;

          await addonSheet.destroy();

          const newOrderPackages = cart.orderPackages.map((op) => {
            const addonSheets = op.addonSheets.filter((as) => as.id.toString() !== id.toString());

            return { ...op, addonSheets };
          });

          cart.orderPackages = newOrderPackages;

          setNumFetches(numFetches + 1);
          setCustomer({ ...customer, cart });
          setCart(cart);
        }

        break;
      }
      default:
        break
    }
  }

  const editItem = (orderPackage) => {
    setCustomer({ ...customer, currentOrderPackage: orderPackage, currentStudent: orderPackage.student })
    history.push(`/background_selection/${orderPackage.package.id}`)
  }

  useEffect(() => {
    const key = customer.priorityCart ? 'priorityCart' : 'cart'
    if (!(customer && customer[key])) {
      setLoading(false)
      setIsLoading(false)
      return
    }

    console.log('Fetching cart data, numFetches:', numFetches);

    const orderPackageScope = OrderPackage.page(page).per(5);

    Cart
      .selectExtra(['subtotal', 'shipping', 'api_tax', 'api_total'])
      .merge({ orderPackages: orderPackageScope })
      .includes([
        'coupon',
        {
          orderPackages: [
            'background',
            'package',
            'seniorImage',
            { student: 'school' },
            'studentImage',
            { api_option: ['image_types', 'package'] },
            { orderPackageExtras: 'extra' },
            { extras: 'prices' },
            { sheets: ['background', { imageType: ['component'] }, 'seniorImage', 'pose', 'foreground'] },
            { addonSheets: ['background', 'seniorImage', { addon: ['component'] }, 'pose', 'foreground'] },
          ],
        },
      ])
      .where({ for_email: [customer[key].id, customer.email], purchased: isPurchased })
      .first()
      .then((res) => {
        console.log('Cart data fetched:', res.data);
        
        // Local state and the shared CustomerContext must end up with the SAME
        // cart. They used to diverge: local state accumulated the pages while
        // the context was handed the RAW page. Order packages load 5 at a time,
        // so every cart ends on a request that returns an empty page -- and that
        // empty page emptied customer.cart.orderPackages. Nothing depended on
        // that value until #5192 gated the checkout CTA on cartHasItems(), at
        // which point the primary Checkout button stopped rendering on every
        // cart that HAS items, while the list beside it still showed them.
        //
        // Page 1 REPLACES so a refetch reconciles against the server (a package
        // deleted server-side must not survive locally); later pages append via
        // mergeCartPage so the final empty page cannot wipe what came before.
        if (res.data) {
          const merged = page === 1 ? res.data : mergeCartPage(cart, res.data);

          setCart(merged);
          setCustomer({ ...customer, [key]: merged });
        }

        if (res.data && res.data.orderPackages.length > 0) {
          setPage((prev) => prev + 1);
        } else if (res.data) {
          setLoading(false);
          setIsLoading(false);
        } else {
          setLoading(false)
          setIsLoading(false)
        }

        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.cart, QUERY_KEYS.numItems] });
      })
      .catch((error) => {
        console.error('Error fetching cart:', error);
        setLoading(false);
        setIsLoading(false);
      });
  }, [numFetches, page])

  return { cart, setCart, loading, removeItem, editItem }
}

const CustomerCart = ({ displayPurchaseInfo, editable, providedCart, children, setIsLoading = () => { },
  showCheckoutLink, spinnerColor = 'white', order, email: emailProp, classes, orderTable = false,
  key = 'cart', isPurchased = false }) => {
  const { cart, setCart, loading, removeItem, editItem } = useCustomerData(providedCart, setIsLoading, isPurchased);

  const [coupon, setCoupon] = useState(cart?.coupon);
  const [isRemoving, setIsRemoving] = useState(false);
  // Set when deleting a base package would strand the senior "Buy All" upsell —
  // holds the pending {id, type} while we confirm with the customer.
  const [pendingRemoval, setPendingRemoval] = useState(null);

  const location = useLocation();

  // Intercept deletes: if removing this order package would leave the senior
  // "Buy All" upsell with no base package, confirm first (Cancel / Delete).
  const requestRemoveItem = (id, type) => {
    if (type === 'orderPackage' && cart?.orderPackages) {
      const target = cart.orderPackages.find((op) => op.id.toString() === id.toString());
      if (target && wouldStrandUpsell(cart.orderPackages, target)) {
        setPendingRemoval({ id, type });
        return;
      }
    }
    removeItem(id, type);
  };

  const confirmRemoval = () => {
    if (!pendingRemoval) return;
    const { id, type } = pendingRemoval;
    setPendingRemoval(null);
    removeItem(id, type);
  };

  useEffect(() => { if (providedCart) setCart(providedCart) }, [providedCart])

  useEffect(() => {
    if (cart?.coupon) setCoupon(cart.coupon);
    else setCoupon(undefined);
  }, [cart?.coupon]);

  if (loading) return <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}><Spinner color={spinnerColor} /></div>;
  if (!orderTable && !cart) return <EmptyCart />
  if (!isPurchased && cart.purchased) return <EmptyCart />
  if (!orderTable && cart.orderPackages.length === 0) return <EmptyCart />;

  const formatCoupon = (cart) => {
    if (cart.coupon.couponType === 'FLAT_RATE') {
      return -cart.coupon.value;
    } else if (cart.coupon.couponType === 'PERCENTAGE') {
      return `${-cart.coupon.value}%`;
    } else if (cart.coupon.couponType === 'FREE_SHIPPING') {
      return -cart.shipping;
    }
    else if (cart.coupon.couponType === 'FLAT_RATE_WITH_FREE_SHIPPING') {
      return `-$${cart.coupon.value} w/ additional -$${cart.shipping}`;
    }
    else if (cart.coupon.couponType === 'PERCENTAGE_WITH_FREE_SHIPPING') {
      return `-${cart.coupon.value}% w/ additional -$${cart.shipping}`;
    }
    else {
      return '';
    }
  };

  const removeCoupon = () => {
    cart.couponId = null

    // Record the explicit removal so the promo auto-apply (useApplyOfferToCart)
    // never re-attaches an offer the customer deliberately took off this cart.
    try { localStorage.setItem(`offerRemoved:${cart.id}`, '1'); } catch (e) { /* private mode */ }

    setIsRemoving(true);

    Cart
      .find(cart.id)
      .then(async (res) => {
        res.data.couponId = null;

        await res.data.save();

        setTimeout(() => {
          window.location.reload();
        }, 500);
      })
      .finally(() => setIsRemoving(false));
  }

  // See couponSummary.js: a shipping-only coupon is presented differently from
  // one that discounts the item prices -- its savings ARE the shipping fee, so
  // that fee is what the Discount line shows (and Cart#coupon_value, which only
  // measures the change in item prices, is not).
  const {
    shippingOnly: shippingOnlyCoupon,
    freeShipping: freeShippingCoupon,
    waivedShipping,
    discountValue,
  } = couponSummary(cart);

  const couponLine = coupon ? couponSummaryLine(coupon, discountValue) : {};

  return (
    <Grid container xs={12} maxWidth='md' classes={cn(styles.container, classes)}>
      {location.pathname.includes('cart') && cart && cart.orderPackages.length > 0 && (
        <V2Button fullWidth style={{ marginBottom: '1rem' }}>
          <Link to={routes.checkout()} style={{ color: '#fff' }}>Checkout</Link>
        </V2Button>
      )}
      {order && order.shippingAddress && !order.hasSeniorBuyAll && (
        <div className={styles.shippingInfo}>
          <Typography classes={styles.shippingLabel} weight={600}>Shipping to:</Typography>
          <div>
            <Typography align="right">{order.firstName}</Typography>
            <Typography align="right">{order.shippingAddress}</Typography>
            <Typography align="right">{order.shippingLineTwo()}</Typography>
          </div>
        </div>
      )}

      {order?.id && (order?.email || emailProp) && (
        <FullResDownloads orderId={order.id} email={order.email || emailProp} />
      )}

      {cart.orderPackages.map(orderPackage => {
        return (
          <Grid container item key={orderPackage.id}>
            <CartItem
              editable={editable}
              orderPackage={orderPackage}
              removeItem={requestRemoveItem}
              editItem={editItem}
              waivesRetouching={!!cart.waivesRetouching}
              key={key}
            />
          </Grid>
        );
      })}

      <Modal
        modalIsOpen={!!pendingRemoval}
        closeModal={() => setPendingRemoval(null)}
        headerText="Remove package?"
        secondaryButton={(
          <Button className="btn btn-default" onClick={() => setPendingRemoval(null)}>Cancel</Button>
        )}
        primaryButton={(
          <Button className="btn btn-danger" onClick={confirmRemoval}>Delete</Button>
        )}
      >
        Buy All Digital Files is not available without purchase of a package.
      </Modal>

      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
        <div className={cn(cartItemStyles.wrapper, styles.stuff)}>
          <SummaryItem text="Subtotal:" value={cart.subtotalWithoutCoupon} />
          {coupon ? (
            <>
              <SummaryItem
                text={couponLine.text}
                value={couponLine.value}
                type="COUPON"
                classes={styles.red}
                useMinus={couponLine.useMinus}
              />
              {!cart.purchased && <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button disabled={isRemoving} type="button" onClick={removeCoupon} style={{ backgroundColor: 'white', color: '#cb444a', textTransform: 'uppercase', padding: '0.5rem', borderRadius: '4px', outline: 'none', border: 'none', fontSize: '.5rem' }}>
                REMOVE COUPON
                </button>
              </div>}
            </>
          ) : (
            <>
              {children}
            </>
          )}
          {coupon && !shippingOnlyCoupon && couponLine.showAfterSavings && <SummaryItem text="Subtotal after Savings:" value={cart.subtotal} />}
          {displayPurchaseInfo && (
            <>
              <SummaryItem
                text='Shipping:'
                value={freeShippingCoupon ? (
                  <span>
                    {waivedShipping > 0 && (
                      <span className={styles.strikethrough} style={{ color: '#999', marginRight: '.4rem' }}>
                        {currency(waivedShipping).format()}
                      </span>
                    )}
                    <span style={{ color: 'red' }}>FREE</span>
                  </span>
                ) : cart.shipping}
              />
              <SummaryItem text='Tax:' value={cart.apiTax} />
            </>
          )}
          {displayPurchaseInfo && <SummaryItem text='Total' value={cart.apiTotal} classes={styles.total} />}
        </div>
        {showCheckoutLink && cart.orderPackages.length > 0 && (
          <Link to='/review' className={styles.link}>
            <Button variant='submit' fullWidth>Proceed to Checkout</Button>
          </Link>
        )}
      </div>
    </Grid>
  )
}

CustomerCart.defaultProps = {
  editable: true,
  displayPurchaseInfo: false,
  showCheckoutLink: false,
}

CustomerCart.propTypes = {
  editable: PropTypes.bool,
  displayPurchaseInfo: PropTypes.bool,
  classes: PropTypes.string,
  showCheckoutLink: PropTypes.bool,
}

export default CustomerCart